import { Stack } from '@mui/material'
import { spacing } from 'c1ui/theme/C1Spacing'
import { RendererProps, resolveDynamicNumber } from 'components/a2ui/types'
import { getByPointer } from 'lib/a2ui/resolver'
import { FC } from 'react'

type ColumnRendererProps = Pick<RendererProps, 'component' | 'dataModel' | 'renderChildren'>

/**
 * Maps A2UI distribution values to MUI justifyContent.
 * Distribution controls spacing along the main axis (vertical for column).
 */
const mapDistribution = (dist: string | undefined): string => {
    switch (dist) {
        case 'start': return 'flex-start'
        case 'end': return 'flex-end'
        case 'center': return 'center'
        case 'space-between': return 'space-between'
        case 'space-around': return 'space-around'
        case 'space-evenly': return 'space-evenly'
        default: return 'flex-start'
    }
}

/**
 * Maps A2UI alignment values to MUI alignItems.
 * Alignment controls positioning along the cross axis (horizontal for column).
 */
const mapAlignment = (align: string | undefined): string => {
    switch (align) {
        case 'start': return 'flex-start'
        case 'end': return 'flex-end'
        case 'center': return 'center'
        case 'stretch': return 'stretch'
        case 'baseline': return 'baseline'
        default: return 'stretch'
    }
}

/** Map pixel gap to C1 spacing token. */
const getSpacing = (px: number): string => {
    if (px <= 4) return spacing.xs
    if (px <= 10) return spacing.s
    if (px <= 16) return spacing.m
    if (px <= 24) return spacing.l
    return spacing.xl
}

/**
 * Renders a vertical column layout using MUI Stack.
 * Maps A2UI layout properties to MUI Stack props:
 * - distribution -> justifyContent (main axis spacing)
 * - alignment -> alignItems (cross axis positioning)
 * - gap -> numeric gap via spacing tokens
 */
export const ColumnRenderer: FC<ColumnRendererProps> = ({ component, dataModel, renderChildren }) => {
    if (component.component.oneofKind !== 'column') return null

    const col = component.component.column
    const gap = resolveDynamicNumber(col.gap, dataModel, getByPointer, 16)
    const children = col.children?.ids ?? []

    return (
        <Stack
            direction="column"
            gap={getSpacing(gap)}
            justifyContent={mapDistribution(col.distribution)}
            alignItems={mapAlignment(col.alignment)}
        >
            {renderChildren(children)}
        </Stack>
    )
}
